import { ErrorHandler, Injectable, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import * as Sentry from '@sentry/angular';
import { environment } from '../../../environments/environment';
import { AuthService } from '../services/auth.service';

@Injectable()
export class SentryErrorHandler implements ErrorHandler {
  private router = inject(Router);
  private authService = inject(AuthService);

  handleError(error: Error | HttpErrorResponse): void {
    // Log to console in development
    if (!environment.production) {
      console.error('Error caught by SentryErrorHandler:', error);
    }

    // Don't send HTTP errors to Sentry (already handled by error interceptor)
    if (error instanceof HttpErrorResponse) {
      // Add breadcrumb for context but don't send as error
      Sentry.addBreadcrumb({
        category: 'http',
        message: `HTTP ${error.status}: ${error.url}`,
        level: 'error',
        data: {
          status: error.status,
          url: error.url,
          method: error.url ? this.extractHttpMethod(error.url) : 'unknown'
        }
      });

      // Only send 5xx errors to Sentry (server errors)
      if (error.status >= 500) {
        this.captureException(error);
      }

      return;
    }

    // Send JavaScript errors to Sentry
    this.captureException(error);
  }

  private captureException(error: any): void {
    if (!environment.sentry.enabled) {
      return;
    }

    Sentry.withScope((scope) => {
      // Add user context
      this.addUserContext(scope);

      // Add additional context
      scope.setContext('app', {
        version: environment.version,
        environment: environment.sentry.environment,
        route: this.router.url
      });

      // Capture the exception
      Sentry.captureException(error);
    });
  }

  private addUserContext(scope: Sentry.Scope): void {
    try {
      const currentUser = this.authService.currentUser();

      if (currentUser) {
        // Only send non-PII user data
        scope.setUser({
          id: String(currentUser.id),
          // DO NOT send email, username, or any PII
        });

        scope.setTag('user_role', currentUser.role);
        scope.setTag('clinic_id', String(currentUser.clinicId));

        scope.setContext('clinic', {
          clinic_id: currentUser.clinicId,
          clinic_name: currentUser.clinicName || 'unknown'
        });
      } else {
        scope.setUser(null);
      }
    } catch (e) {
      // Silently fail if unable to get user context
      console.debug('Failed to add user context to Sentry', e);
    }
  }

  private extractHttpMethod(url: string): string {
    // This is a simplified approach
    // In a real scenario, you'd need to track the method separately
    return 'unknown';
  }
}
